import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { BookOpen, Loader2, Lock, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

export default function ResetPassword() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) {
      toast.error("Las contraseñas no coinciden");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      toast.success("Contraseña actualizada");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "No se pudo restablecer la contraseña");
    } finally {
      setLoading(false);
    }
  };

  const field =
    "w-full rounded-xl border-2 border-[var(--lc-border)] focus:border-[var(--lc-primary)] focus:ring-4 focus:ring-[var(--lc-primary)]/20 outline-none transition-colors pl-11 pr-4 p-3.5 bg-[var(--lc-bg)]";

  return (
    <div className="min-h-screen bg-[var(--lc-bg)] lc-dots grid place-items-center px-4">
      <div className="w-full max-w-md bg-white rounded-3xl border border-[var(--lc-border)] shadow-xl p-8 sm:p-10 lc-fade-up" data-testid="reset-password-page">
        <div className="flex items-center gap-3 mb-6">
          <img src="/logo-icon.png" alt="Edibrante" className="w-12 h-12 object-contain select-none" draggable="false" />
          <div>
            <p className="font-display font-extrabold text-xl text-[var(--lc-ink)]">Nueva contraseña</p>
            <p className="text-sm text-[var(--lc-slate)]">Edibrante</p>
          </div>
        </div>

        {!token ? (
          <p className="text-[var(--lc-slate)]" data-testid="reset-invalid">
            El enlace no es válido. Solicita uno nuevo desde "¿Olvidaste tu contraseña?".
          </p>
        ) : done ? (
          <div className="text-center" data-testid="reset-success">
            <CheckCircle2 className="w-14 h-14 text-[var(--lc-success)] mx-auto mb-3" />
            <p className="font-display font-bold text-lg text-[var(--lc-ink)]">¡Listo!</p>
            <p className="text-[var(--lc-slate)] mt-1 mb-6">
              Tu contraseña se actualizó. Ya puedes iniciar sesión con la nueva.
            </p>
            <button
              onClick={() => nav("/")}
              data-testid="reset-go-home"
              className="w-full rounded-full bg-[var(--lc-primary)] text-white py-3.5 font-display font-extrabold text-lg hover:-translate-y-0.5 transition-transform"
            >
              Ir al inicio
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <div className="relative">
              <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--lc-slate)]" />
              <input
                type="password"
                className={field}
                data-testid="reset-password-input"
                placeholder="Nueva contraseña (mín. 6)"
                required
                minLength={6}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            <div className="relative">
              <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--lc-slate)]" />
              <input
                type="password"
                className={field}
                data-testid="reset-confirm-input"
                placeholder="Confirmar contraseña"
                required
                minLength={6}
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              data-testid="reset-submit"
              className="w-full flex items-center justify-center gap-2 rounded-full bg-[var(--lc-primary)] text-white py-3.5 font-display font-extrabold text-lg hover:-translate-y-0.5 transition-transform disabled:opacity-60"
            >
              {loading && <Loader2 className="w-5 h-5 animate-spin" />}
              Guardar contraseña
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
